import { useEffect, useRef, useState } from 'react';

import { type Masked } from '~/contentful/graphql';
import { getDuration, getMultiplier, multiplyChildren } from '~/utils/marquee';

import styles from './marquee.module.scss';
import { Idea } from '../idea';
import { type Idea_IdeaFragment } from '../idea/query';

export interface IdeasMarqueeProps {
  ideas: Masked<typeof Idea_IdeaFragment>[];

  /** CSS animation-direction property */
  direction: React.CSSProperties['animationDirection'];

  /** Pixels per second */
  speed: number;
}

export function IdeasMarquee({ ideas, direction, speed }: IdeasMarqueeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const marqueeInnerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [marqueeInnerWidth, setMarqueeInnerWidth] = useState(0);
  const [playing, setPlaying] = useState(true);

  const mapIdeas = () =>
    ideas.map(
      (idea, index) =>
        idea && (
          <Idea
            idea={idea}
            onMouseOver={() => setPlaying(false)}
            onFocus={() => setPlaying(false)}
            onMouseOut={() => setPlaying(true)}
            onBlur={() => setPlaying(true)}
            key={index}
            showSeparator
          />
        ),
    );

  useEffect(() => {
    const container = containerRef.current;
    const marqueeInner = marqueeInnerRef.current;
    if (!container || !marqueeInner) return;

    const resizeObserver = new ResizeObserver(() => {
      const containerRect = container.getBoundingClientRect();
      const marqueeRect = marqueeInner.getBoundingClientRect();
      setContainerWidth(containerRect.width);
      setMarqueeInnerWidth(marqueeRect.width);
    });
    resizeObserver.observe(container);
    resizeObserver.observe(marqueeInner);
    return () => resizeObserver.disconnect();
  }, []);

  const multiplier = getMultiplier(marqueeInnerWidth, containerWidth);
  const duration = getDuration(marqueeInnerWidth, multiplier, speed);

  const marqueeStyle: React.CSSProperties = {
    animationDirection: direction,
    animationDuration: `${duration}s`,
    animationPlayState: playing ? 'running' : 'paused',
    animationDelay: `${-10 * speed}s`,
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.marquee} style={marqueeStyle}>
        <div className={styles.marqueeInner} ref={marqueeInnerRef}>
          {mapIdeas()}
        </div>
        {multiplyChildren(multiplier - 1, mapIdeas())}
      </div>
      <div className={styles.marquee} style={marqueeStyle}>
        {multiplyChildren(multiplier, mapIdeas())}
      </div>
    </div>
  );
}
